import { diContainer } from '../DIContainer';
import { AppStorageKey } from '../types';
import { apiService } from './ApiService';
import { fiscalService } from '../fiscal/FiscalService';
import { websocketsService } from './WebsocketsService';
import { localNetworkService } from './LocalNetworkService';
import { printService } from '../print/PrintService';
import TenantService from './TenantService';
import UserService from './UserService';
import CertificateService from './CertificateService';
import ConnectionService from './ConnectionService';
import { modelRegistry } from '../models/Model';
import User from '../models/User';
import Role from '../models/Role';
import Product from '../models/Product';
import TaxRate from '../models/TaxRate';
import TaxItem from '../models/TaxItem';
import ProductCategory from '../models/ProductCategory';
import Order from '../models/Order';
import OrderItem from '../models/OrderItem';
import OrderItemComment from '../models/OrderItemComment';
import Client from '../models/Client';
import Sector from '../models/Sector';
import Table from '../models/Table';
import Grid from '../models/Grid';
import Button from '../models/Button';
import Shift from '../models/Shift';
import CashRegister from '../models/CashRegister';
import CashRegisterTransaction from '../models/CashRegisterTransaction';
import UserTransaction from '../models/UserTransaction';
import ProductIngredient from '../models/ProductIngredient';
import Session from '../models/Session';
import Shortcut from '../models/Shortcut';
import Pricelist from '../models/Pricelist';
import Notification from '../models/Notification';
import KitchenTicket from '../models/KitchenTicket';
import OrderTicket from '../models/OrderTicket';
import OrderRequest from '../models/OrderRequest';
import Terminal from '../models/Terminal';
import SyncData from '../models/SyncData';
import AppEvent from '../models/AppEvent';
import TableSession from '../models/TableSession';
import BeachBooking from '../models/BeachBooking';
import PaymentSession from '../models/PaymentSession';
import Store from '../wrappers/Store';
import Locale from '../wrappers/Locale';
import { CapacitorPrint } from 'capacitor-print';
import { CapacitorSdc } from 'capacitor-sdc';
import { I18n } from '../wrappers/I18n';
import { HashGen, Money, Device } from '../utilities';
import { Settings } from '../wrappers';
import { BarcodeScanner } from '@capacitor-mlkit/barcode-scanning';
import TerminalService from './TerminalService';
import moment from 'moment-timezone';
export class AppService {
    static async initializeAdminApp(options = {}) {
        // Detect device
        const device = diContainer.resolve('device');
        const deviceInfo = await device.detect();
        // Initialize storage
        const appStorage = diContainer.resolve('appStorage');
        await appStorage.init(deviceInfo);
        // Explicit null prefix: the backoffice flag is APP-scoped, not tenant-scoped.
        // Without it the key is prefix-factory'd — bare at this pre-tenant write but
        // `<venue.hash>-backoffice` at any post-setTenant read (FiscalService.init),
        // which silently broke the isAdmin guard there. Pinned by FiscalServiceInit.spec.
        await appStorage.local().setItem(AppStorageKey.backoffice, true, null);
        // Initialize the store from storage
        await this.initializeStore();
        // Set the device
        device.set(deviceInfo);
        // Detect locale and set it
        let locale = options.locale || await Locale.get();
        if (!locale) {
            locale = await Locale.detect();
        }
        await Locale.set(locale);
        // Initialize services
        websocketsService.init();
        // Set tenant and user
        await TenantService.setTenant(Store.get('tenant'), false);
        await UserService.setUser(Store.get('user'));
        let config = undefined;
        if (options.tntoken) {
            await TenantService.removeTenant();
            await UserService.removeUser();
            await TenantService.setToken(options.tntoken);
            if (options.token)
                await UserService.setToken(options.token);
            config = {
                headers: {
                    'TnAuthorization': `Tenant ${options.tntoken}`,
                    'Authorization': options.token ? `Bearer ${options.token}` : undefined,
                }
            };
        }
        // Get the initial data from server
        const response = await apiService.get('/initialize', config);
        // Set tenant and user
        await TenantService.setTenant(response.data.tenant);
        await UserService.setUser(response.data.user);
        Store.set('modules', response.data.modules || {});
        Store.set('integrations', response.data.integrations || {});
        Store.set('ordersHubActive', response.data.ordersHubActive ?? false);
        if (options.tntoken) {
            Store.set('redirect', true);
        }
        // Store app config
        if (response.data.appConfig) {
            Store.set('appConfig', response.data.appConfig);
        }
        try {
            if (response.data.tenant) {
                await TenantService.loadAdminData();
            }
        }
        catch (error) {
            console.error(error.message);
        }
    }
    static async initializeApp(options = {}) {
        // Detect device
        const device = diContainer.resolve('device');
        const deviceInfo = await device.detect();
        // Initialize storage
        const appStorage = diContainer.resolve('appStorage');
        await appStorage.init(deviceInfo);
        // Initialize the store from storage
        await this.initializeStore();
        // Set the device
        device.set(deviceInfo);
        // Detect locale and set it
        let locale = await Locale.get();
        if (!locale) {
            locale = await Locale.detect();
        }
        await Locale.set(locale);
        // Initialize services
        websocketsService.init();
        localNetworkService.init();
        // Ask for permissions
        AppService.checkPermissions();
        // Set tenant and user
        await TenantService.setTenant(Store.get('tenant'), false);
        await UserService.setUser(Store.get('user'));
        // Check connection (defaults to true)
        const isOnline = await ConnectionService.connectionCheck();
        if (!isOnline) {
            TenantService.startTenantServices();
            return;
        }
        let config = undefined;
        if (options.tntoken) {
            await TenantService.removeTenant();
            await UserService.removeUser();
            await TenantService.setToken(options.tntoken);
            if (options.token)
                await UserService.setToken(options.token);
            config = {
                headers: {
                    'TnAuthorization': `Tenant ${options.tntoken}`,
                    'Authorization': options.token ? `Bearer ${options.token}` : undefined,
                }
            };
        }
        // Get the initial data from server
        const response = await apiService.get('/initialize', config);
        // Set tenant and user
        await TenantService.setTenant(response.data.tenant);
        await UserService.setUser(response.data.user);
        // set terminal
        const terminalId = await TerminalService.getId();
        if (!terminalId && response.data.cashRegister?.terminal) {
            TerminalService.setTerminal(response.data.cashRegister.terminal, response.data.cashRegister);
        }
        // Store app config
        if (response.data.appConfig) {
            Store.set('appConfig', response.data.appConfig);
        }
        Store.set('ordersHubActive', response.data.ordersHubActive ?? false);
        try {
            if (response.data.tenant) {
                await TenantService.loadData(true);
            }
        }
        catch (error) {
            console.error(error.message);
        }
    }
    static async initializeStore() {
        const appStorage = diContainer.resolve('appStorage');
        const state = {};
        const setStoreItem = async (key, defaultValue, model) => {
            const finalKey = key !== undefined ? key : (model?.entity ? `entities.${model.entity}` : undefined);
            if (!finalKey)
                return;
            let value = await appStorage.store().getItem(finalKey);
            if (value === null) {
                value = defaultValue;
            }
            if (model) {
                model.init({ data: value });
            }
            else {
                state[finalKey] = value;
            }
        };
        await Promise.all([
            setStoreItem(undefined, [], User),
            setStoreItem(undefined, [], Role),
            setStoreItem(undefined, [], Session),
            setStoreItem(undefined, [], Product),
            setStoreItem(undefined, [], ProductCategory),
            setStoreItem(undefined, [], ProductIngredient),
            setStoreItem(undefined, [], TaxRate),
            setStoreItem(undefined, [], Order),
            setStoreItem(undefined, [], OrderItem),
            setStoreItem(undefined, [], OrderItemComment),
            setStoreItem(undefined, [], Client),
            setStoreItem(undefined, [], Sector),
            setStoreItem(undefined, [], Table),
            setStoreItem(undefined, [], Grid),
            setStoreItem(undefined, [], Button),
            setStoreItem(undefined, [], Shift),
            setStoreItem(undefined, [], CashRegister),
            setStoreItem(undefined, [], CashRegisterTransaction),
            setStoreItem(undefined, [], Shortcut),
            setStoreItem(undefined, [], UserTransaction),
            setStoreItem(undefined, [], Pricelist),
            setStoreItem(undefined, [], Notification),
            setStoreItem(undefined, [], KitchenTicket),
            setStoreItem(undefined, [], OrderTicket),
            setStoreItem(undefined, [], Terminal),
            setStoreItem(undefined, [], SyncData),
            setStoreItem(undefined, [], TableSession),
            setStoreItem(undefined, [], PaymentSession),
            setStoreItem(undefined, [], BeachBooking),
            setStoreItem('lastTenant', null),
            setStoreItem('tenant', null),
            setStoreItem('user', null),
            setStoreItem('curOrder', null),
            setStoreItem('curUnpaidOrders', []),
            setStoreItem('curPayOptions', Order.getPayOptions()),
            setStoreItem('cashRegister', null),
            setStoreItem('terminal', null),
            setStoreItem('appConfig', null),
            setStoreItem('printConfig', null),
            setStoreItem('device', null),
            setStoreItem('workingDays', []),
            // The last online load's modules: an offline boot must not read every module as off
            // (the till's inventory-accounting price lock among them).
            setStoreItem('modules', {}),
            setStoreItem('cardSettlement', { lastSettledAt: null, unsettledPayments: false }),
            setStoreItem('pendingOrderData', null),
            setStoreItem('pausedOrderData', null),
            setStoreItem('curPricelist', 0),
            setStoreItem('curDiscountPercent', 0),
            // null = the user never chose: the app's default applies (store getter).
            setStoreItem('darkTheme', null),
            setStoreItem('isRetail', false),
            setStoreItem('ordersHubActive', false),
            setStoreItem('redirect', false),
            setStoreItem('debugMode', false),
            setStoreItem('log', []),
            setStoreItem('kdsSettings', {
                itemsPerPage: [3, 4],
                fontSize: 'medium',
                yellowTimer: 5,
                redTimer: 7,
                filterForUser: false
            }),
        ]);
        if (state.curOrder)
            state.curOrder = new Order(state.curOrder);
        if (state.curUnpaidOrders)
            state.curUnpaidOrders = state.curUnpaidOrders.map((order) => new Order(order));
        if (state.curPayOptions?.client)
            state.curPayOptions.client = new Client(state.curPayOptions.client);
        if (state.curPayOptions?.table)
            state.curPayOptions.table = new Table(state.curPayOptions.table);
        if (state.cashRegister)
            state.cashRegister = new CashRegister(state.cashRegister);
        if (state.terminal)
            state.terminal = new Terminal(state.terminal);
        if (state.pendingOrderData?.order)
            state.pendingOrderData.order = new Order(state.pendingOrderData.order);
        if (state.pausedOrderData?.order)
            state.pausedOrderData.order = new Order(state.pausedOrderData.order);
        if (state.pausedOrderData?.pending?.order)
            state.pausedOrderData.pending.order = new Order(state.pausedOrderData.pending.order);
        Store.setState(state);
    }
    /**
     * Perform offline data sync
     */
    static async sync(fromBackupStore = false, force = false) {
        const syncOptions = Store.get('syncOptions');
        let syncStatus = Store.get('syncStatus');
        const tenant = Store.get('tenant');
        const venue = tenant?.venue || null;
        const autoSyncDisabled = !force && !!syncOptions.autoSyncDisabled;
        if (!tenant || !venue || syncStatus === 'inProgress' || autoSyncDisabled) {
            return {
                success: false,
                details: `Sync denied. Reason:${!tenant ? ' tenant not set' : ''}` +
                    `${!venue ? ' venue not set' : ''}` +
                    `${syncStatus === 'inProgress' ? ' sync in progress' : ''}` +
                    `${autoSyncDisabled ? ' autoSyncDisabled set' : ''}`
            };
        }
        const syncData = await SyncData.getDataForSync(fromBackupStore);
        const syncDataCount = syncData.length;
        if (!syncData.length) {
            Store.set('syncStatus', 'done');
            return {
                success: false,
                details: 'No data to sync',
            };
        }
        syncStatus = 'inProgress';
        Store.set('syncStatus', syncStatus);
        try {
            while (syncData.length) {
                await this.syncWithServer(syncData);
            }
            const remainingSyncData = await SyncData.getDataForSync(fromBackupStore);
            if (!fromBackupStore) {
                SyncData.deleteAll();
                modelRegistry.getSyncableModelClasses().forEach(modelClass => {
                    let unsyncedModelsExist = modelClass.all().some(model => !model.id);
                    if (unsyncedModelsExist) {
                        const syncedModels = modelClass.all().filter(model => !!model.id);
                        modelClass.create({ data: syncedModels.map(model => model.$toJson()) });
                    }
                });
            }
            syncStatus = 'done';
            return {
                success: true,
                details: `Synced ${syncDataCount - remainingSyncData.length} items. Not Synced: ${remainingSyncData.length}`
            };
        }
        catch (error) {
            syncStatus = 'failed';
            console.log(error.message);
            const errorReporter = diContainer.resolve('errorReporter');
            errorReporter.reportMessage('Sync failed', { error: error.message });
            throw {
                message: 'sync_exception',
                details: error.message
            };
        }
        finally {
            Store.set('syncStatus', syncStatus);
        }
    }
    /**
     * Sync with server
     */
    static async syncWithServer(syncData) {
        const syncOptions = Store.get('syncOptions');
        const timeout = syncOptions.timeout || 120000;
        const limit = syncOptions.limit || 50;
        const dataToSend = syncData.slice(0, limit);
        const response = await apiService.post('/sync-data', { syncData: dataToSend }, { timeout: timeout });
        syncData.splice(0, limit);
        if (response.data.failedItems.length) {
            console.error('SYNC ERROR', response.data.failedItems);
        }
        await SyncData.deleteItems(response.data.syncedItems.map(i => i.item));
        await SyncData.deleteItemsBackup(response.data.syncedItems.map(i => i.item));
        await SyncData.deleteModelsBackup(Object.values(response.data.additionalModels).flat());
        const syncedModels = response.data.syncedModels;
        const additionalModels = response.data.additionalModels;
        const modelNames = [...Object.keys(syncedModels), ...Object.keys(additionalModels)]
            .filter((key, index, array) => array.indexOf(key) === index);
        modelNames.forEach(modelName => {
            const ModelClass = modelRegistry.getModelClass(modelName);
            const records = [...(syncedModels[modelName] || []), ...(additionalModels[modelName] || [])];
            // The synced orders' products are not a product load: their time (the sync's) would
            // move this terminal's next load, and its peers', past changes made elsewhere since.
            const models = modelName === 'Product' ? Product.withoutLoadWatermark(records) : records;
            ModelClass?.insert({ data: models });
            // One item per record — a single item carrying the whole array
            // would be $fill'ed as one record on peer terminals, producing a
            // default-filled phantom row (random hash, status 'accepted').
            ModelClass?.syncTerminals({ items: models.map(record => ({ action: 'patch', record })) });
        });
        return response.data;
    }
    static async checkPermissions() {
        let printPermissions = await CapacitorPrint.checkPermissions();
        if (printPermissions.bluetooth !== 'granted') {
            printPermissions = await CapacitorPrint.requestPermissions();
        }
        let sdcPermissions = await CapacitorSdc.checkPermissions();
        if (sdcPermissions.esdc !== 'granted') {
            sdcPermissions = await CapacitorSdc.requestPermissions();
        }
        let barcodePermissions = { camera: 'granted' };
        if (Device.isMobile()) {
            barcodePermissions = await BarcodeScanner.checkPermissions();
            if (barcodePermissions.camera !== 'granted') {
                barcodePermissions = await BarcodeScanner.requestPermissions();
            }
            // Check if Google Barcode Scanner module is available (Android only)
            if (Device.isAndroid()) {
                const { available } = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
                if (!available) {
                    await BarcodeScanner.installGoogleBarcodeScannerModule();
                }
            }
        }
        return {
            bluetooth: printPermissions.bluetooth === 'granted',
            esdc: sdcPermissions.esdc === 'granted',
            camera: barcodePermissions.camera === 'granted',
        };
    }
    static async goToAdministration() {
        const tenantToken = await TenantService.getToken();
        const userToken = await UserService.getToken();
        const locale = await Locale.get();
        const adminUrl = `${Locale.getAdminDomain()}/admin/dashboard?tntoken=${tenantToken}&token=${userToken}&locale=${locale}`;
        if (Device.isElectron()) {
            // In Electron, use window.open with _self to force a clean navigation
            window.open(adminUrl, '_self');
        }
        else {
            location.href = adminUrl;
        }
    }
    static setUpDevelopmentMode() {
        // if (Settings.env.ENV === 'production') return
        const appStorage = diContainer.resolve('appStorage');
        //@ts-ignore
        window.User = User;
        //@ts-ignore
        window.Notification = Notification;
        //@ts-ignore
        window.Role = Role;
        //@ts-ignore
        window.Product = Product;
        //@ts-ignore
        window.TaxRate = TaxRate;
        //@ts-ignore
        window.TaxItem = TaxItem;
        //@ts-ignore
        window.ProductCategory = ProductCategory;
        //@ts-ignore
        window.Order = Order;
        //@ts-ignore
        window.OrderItem = OrderItem;
        //@ts-ignore
        window.OrderItemComment = OrderItemComment;
        //@ts-ignore
        window.Client = Client;
        //@ts-ignore
        window.Sector = Sector;
        //@ts-ignore
        window.Table = Table;
        //@ts-ignore
        window.Grid = Grid;
        //@ts-ignore
        window.Button = Button;
        //@ts-ignore
        window.Shift = Shift;
        //@ts-ignore
        window.CashRegister = CashRegister;
        //@ts-ignore
        window.CashRegisterTransaction = CashRegisterTransaction;
        //@ts-ignore
        window.UserTransaction = UserTransaction;
        //@ts-ignore
        window.Product = Product;
        //@ts-ignore
        window.KitchenTicket = KitchenTicket;
        //@ts-ignore
        window.ProductCategory = ProductCategory;
        //@ts-ignore
        window.ProductIngredient = ProductIngredient;
        //@ts-ignore
        window.Session = Session;
        //@ts-ignore
        window.Shortcut = Shortcut;
        //@ts-ignore
        window.Pricelist = Pricelist;
        //@ts-ignore
        window.OrderTicket = OrderTicket;
        //@ts-ignore
        window.OrderRequest = OrderRequest;
        //@ts-ignore
        window.Store = Store;
        //@ts-ignore
        window.AppEvent = AppEvent;
        //@ts-ignore
        window.Terminal = Terminal;
        //@ts-ignore
        window.SyncData = SyncData;
        //@ts-ignore
        window.AppStorage = appStorage;
        //@ts-ignore
        window.Locale = Locale;
        //@ts-ignore
        window.TerminalService = TerminalService;
        //@ts-ignore
        window.printService = printService;
        //@ts-ignore
        window.I18n = I18n;
        //@ts-ignore
        window.AppService = AppService;
        //@ts-ignore
        window.moment = moment;
        //@ts-ignore
        window.HashGen = HashGen;
        //@ts-ignore
        window.Settings = Settings;
        //@ts-ignore
        window.apiService = apiService;
        //@ts-ignore
        window.fiscalService = fiscalService;
        //@ts-ignore
        window.UserService = UserService;
        //@ts-ignore
        window.CertificateService = CertificateService;
        //@ts-ignore
        window.Money = Money;
        if (Settings.env.ENV === 'development') {
            Store.set('modules', {
                'inventory_accounting': false,
                'einvoices': true,
                'ordering_devices': true,
                'kds': true,
                'product_buttons': true,
                'product_ingredients': true,
                'pricelists': true,
                'delivery': true
            });
            Store.set('ordersHubActive', true);
        }
    }
}
export default AppService;
//# sourceMappingURL=AppService.js.map